import React from "react";
import { useScrollReveal } from "../NewHomePage/useAnimations";
import { PRACTICES as PRACTICES_DATA } from "../../data/practices";
import "./Family.scss";

const ArrowIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

/* Decorative editorial line-art — abstract, not literal icons */
const GrowthIllustration = () => (
  <svg
    className="abt-family__illustration"
    width="150"
    height="150"
    viewBox="0 0 150 150"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
  >
    <circle cx="75" cy="75" r="72" strokeWidth="1.2" />
    <polyline
      points="34,102 60,76 78,94 116,48"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="94,48 116,48 116,70"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="34" cy="102" r="3.2" fill="currentColor" stroke="none" />
    <circle cx="78" cy="94" r="3.2" fill="currentColor" stroke="none" />
    <circle cx="116" cy="48" r="4" fill="currentColor" stroke="none" />
  </svg>
);

const VentureIllustration = () => (
  <svg
    className="abt-family__illustration"
    width="150"
    height="150"
    viewBox="0 0 150 150"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
  >
    <circle cx="75" cy="75" r="72" strokeWidth="1.2" />
    <circle cx="75" cy="75" r="42" strokeWidth="1.2" />
    <path
      d="M75 33 L84 66 L117 75 L84 84 L75 117 L66 84 L33 75 L66 66 Z"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <circle cx="75" cy="75" r="5" fill="currentColor" stroke="none" />
  </svg>
);

const practices = [
  {
    index: "01",
    illustration: GrowthIllustration,
    tag: "growRyte",
    kicker: "Product Growth Engineering",
    title: "Your product has stopped growing. We find out why, then fix it.",
    desc:
      "growRyte diagnoses exactly where activation, engagement, retention or conversion is leaking value, using real product data, then implements the fix directly, inside your existing product.",
    href: "/growryte",
  },
  {
    index: "02",
    illustration: VentureIllustration,
    tag: "ventureRyte",
    kicker: "Venture Capital 2.0",
    title: "Where technology, capital and strategy converge.",
    desc:
      "ventureRyte combines Big-4-grade strategy, senior engineering and MMP-first execution to help founders build products that scale, raise, and win: capability, not just cash.",
    href: "/ventureryte",
  },
];

/* Same cards, with the services-page "Practices" content (used on the home
   page). Copy comes from data/practices.js so it stays in sync. */
const PRACTICE_ILLUSTRATIONS = {
  "/growryte": GrowthIllustration,
  "/ventureryte": VentureIllustration,
};

const practicesVariant = PRACTICES_DATA.map((p, i) => ({
  index: String(i + 1).padStart(2, "0"),
  illustration: PRACTICE_ILLUSTRATIONS[p.to],
  tag: p.label,
  kicker: "Specialist Practice",
  desc: p.desc,
  href: p.to,
  learn: "Learn more",
}));

const HEADINGS = {
  about: {
    label: "Beyond Consulting",
    title: "One team. Two specialist practices.",
    subtitle: (
      <>
        digiRyte isn&rsquo;t a single service. It&rsquo;s a small group of
        senior-led practices, each built around a different stage of a
        company&rsquo;s journey.
      </>
    ),
    items: practices,
  },
  practices: {
    label: "Practices",
    title: "Beyond Consult, Audit, Build, Scale.",
    subtitle:
      "Two specialist practices, each drawing on all four core capabilities to solve a specific commercial problem.",
    items: practicesVariant,
  },
};

const Family = ({ variant = "about" }) => {
  const [ref, vis] = useScrollReveal({ threshold: 0.1 });
  const v = vis ? " is-visible" : "";
  const content = HEADINGS[variant] || HEADINGS.about;

  return (
    <section className="abt-family" ref={ref}>
      <div className="abt-family__inner">
        <div className={`abt-family__head${v}`}>
          <span className="abt-family__label">{content.label}</span>
          <h2 className="abt-family__title">{content.title}</h2>
          <p className="abt-family__subtitle">{content.subtitle}</p>
        </div>

        <div className="abt-family__grid">
          {content.items.map((p, i) => {
            const Illustration = p.illustration;
            return (
              <a
                key={p.tag}
                href={p.href}
                className={`abt-family__card${v}`}
                style={{ transitionDelay: `${0.1 + i * 0.1}s` }}
              >
                <span className="abt-family__index">{p.index}</span>
                <Illustration />

                <span className="abt-family__tag">
                  <span className="abt-family__dot" />
                  {p.tag}
                </span>
                <span className="abt-family__kicker">{p.kicker}</span>
                {p.title && (
                  <h3 className="abt-family__card-title">{p.title}</h3>
                )}
                <p className="abt-family__desc">{p.desc}</p>

                <span className="abt-family__learn">
                  {p.learn || `Explore ${p.tag}`} <ArrowIcon />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Family;
